import { useCallback, useState, useEffect, useRef } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Search, X } from 'lucide-react'
import { useTranslations } from 'use-intl'
import { clearDiscoverySelection } from '../../lib/filter-state'

export function GlobalSearchBar() {
  const search = useSearch({ strict: false }) as any
  const navigate = useNavigate()
  const [value, setValue] = useState(search?.q || '')
  const timerRef = useRef<any>(undefined)
  const lastPushedQ = useRef<string | undefined>(search?.q)
  const t = useTranslations('discovery')

  const pushSearch = useCallback(
    (q: string) => {
      lastPushedQ.current = q || undefined
      navigate({
        search: (prev: any) => ({
          ...clearDiscoverySelection(prev),
          q: q || undefined,
        }),
        replace: true,
      } as any)
    },
    [navigate],
  )

  useEffect(() => {
    clearTimeout(timerRef.current)

    if (value === (search?.q || '')) {
      return
    }

    timerRef.current = setTimeout(() => pushSearch(value), 350)
    return () => clearTimeout(timerRef.current)
  }, [value, search?.q, pushSearch])

  // Sync external URL q-param changes (e.g. browser back/forward, navigate from other routes)
  // Only update local value if the external query doesn't match our last pushed value
  useEffect(() => {
    const externalQ = search?.q || ''
    if (search?.q !== lastPushedQ.current) {
      setValue(externalQ)
      lastPushedQ.current = search?.q
    }
  }, [search?.q])

  return (
    <div className="relative flex items-center w-full max-w-md">
      <Search className="absolute left-3 w-4 h-4 text-stone-400 pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t('searchPlaceholder', { defaultValue: 'Search restaurants...' })}
        aria-label={t('searchPlaceholder', { defaultValue: 'Search restaurants...' })}
        className="w-full pl-9 pr-8 py-2 text-sm rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 focus:outline-none focus:ring-2 focus:ring-primary"
        data-testid="global-search-input"
      />
      {value && (
        <button
          type="button"
          onClick={() => setValue('')}
          className="absolute right-1 flex h-8 w-8 items-center justify-center rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-600"
          aria-label={t('clearSearch', { defaultValue: 'Clear search' })}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}
